/**
 * Platform Detection Utility
 * Detects if app is running as iOS native app or web browser
 * Used to implement Apple App Store "reader app" compliance
 */

import { Capacitor } from '@capacitor/core';
import { Browser } from '@capacitor/browser';
import { browserAppBase, PRODUCTION_SITE } from '../shared/siteUrl';
import { devLog } from './devLog';

/** Plain text shown in the native app. It is not a link and not a buy button. */
export const NATIVE_PLAN_NOTICE = 'Manage your plan at app.pacarion.com';

/**
 * Check if running as native iOS app
 * @returns {boolean} True if running in Capacitor iOS app
 */
export const isNativeApp = () => {
  return Capacitor.isNativePlatform();
};

/**
 * Check if running as web app (browser)
 * @returns {boolean} True if running in web browser
 */
export const isWebApp = () => {
  return !Capacitor.isNativePlatform();
};

/**
 * Get platform name
 * @returns {string} 'ios', 'android', or 'web'
 */
export const getPlatform = () => {
  return Capacitor.getPlatform();
};

/**
 * Check if running on iOS specifically
 * @returns {boolean} True if running on iOS (native or web)
 */
export const isIOS = () => {
  const platform = Capacitor.getPlatform();
  return platform === 'ios';
};

/**
 * Check if running on Android specifically
 * @returns {boolean} True if running on Android
 */
export const isAndroid = () => {
  const platform = Capacitor.getPlatform();
  return platform === 'android';
};

/**
 * Check if payment/subscription features should be shown
 * Apple App Store requires all in-app subscriptions use Apple IAP
 * We implement "reader app" model: users subscribe on web, consume in app
 *
 * @returns {boolean} True if payment UI should be shown
 */
export const canShowPaymentUI = () => {
  // Only show payment UI in web app (not in native iOS/Android apps)
  return isWebApp();
};

/**
 * Get website URL for subscriptions
 * Used in native apps to direct users to website for payment
 *
 * @returns {string} Website URL
 */
/**
 * Native API base is REACT_APP_APP_URL, baked in at build time.
 * When that variable is unset, the fallback is https://app.pacarion.com
 * (PRODUCTION_SITE). pacarion.com is the marketing site and does not serve
 * Netlify functions. The web app uses a relative path so Netlify redirects
 * stay on the current host, including a develop deploy.
 */
export const getApiUrl = (path) => {
  if (isNativeApp()) {
    const configured = String(process.env.REACT_APP_APP_URL || '').trim().replace(/\/$/, '');
    return `${configured || PRODUCTION_SITE}${path}`;
  }
  return path;
};

export const getWebsiteURL = () => {
  if (isNativeApp()) {
    const configured = String(process.env.REACT_APP_APP_URL || '').trim().replace(/\/$/, '');
    return configured || PRODUCTION_SITE;
  }
  if (typeof window !== 'undefined' && window.location) {
    return browserAppBase(window.location);
  }
  if (process.env.NODE_ENV !== 'production') return 'http://localhost:3000';
  return PRODUCTION_SITE;
};

/**
 * Redirect to website (for native apps)
 * Opens website in system browser
 */
export const redirectToWebsite = () => {
  const url = getWebsiteURL();
  if (isNativeApp()) {
    const open = Browser && typeof Browser.open === 'function'
      ? Browser.open({ url })
      : Promise.reject(new Error('Browser unavailable'));
    Promise.resolve(open).catch(() => {
      window.open(url, '_blank', 'noopener,noreferrer');
    });
    return;
  }
  window.location.href = url;
};

/**
 * Platform-specific console log
 * Helps debug platform detection issues
 */
export const logPlatformInfo = () => {
  devLog('Platform Info:', {
    isNativeApp: isNativeApp(),
    isWebApp: isWebApp(),
    platform: getPlatform(),
    isIOS: isIOS(),
    isAndroid: isAndroid(),
    canShowPaymentUI: canShowPaymentUI(),
    websiteURL: getWebsiteURL()
  });
};
